import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import dayjs from 'dayjs';
import { useGetInvoicePaymentHistoryQuery } from '../../Service/extendedInvoicesApi';
import RecordPaymentDialog from './RecordPaymentDialog';
import RecordRefundDialog from './RecordRefundDialog';
import type { InvoicePaymentDto, PaymentSummary } from '../../Types/paymentTypes';
import { PaymentMethodTypeLabels } from '../../Types/invoicesLabels';
import { PaymentMethodType } from '../../Types/invoicesTypes';
import { useNotification } from '../../Context/NotificationContext';
import { invoiceLogger } from '../../utils/invoiceLogger';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';

/** Set when a correction reports the payment made on its base invoice instead of own payments. */
export interface SettledOnBaseInvoice {
  baseInvoiceNumber?: string | null;
  paidAt?: string | null;
}

interface PaymentSectionProps {
  invoiceId: number;
  totalGross: number;
  paidAmount: number;
  settledOnBaseInvoice?: SettledOnBaseInvoice;
}

/**
 * Payment section component for invoice details page.
 * Displays payment history, summary, and actions (record payment/refund).
 * For a correction settled on its base invoice it shows that settlement instead of an unpaid
 * balance and offers no payment actions (the payment is booked once, on the base invoice).
 */
const PaymentSection: React.FC<PaymentSectionProps> = ({
  invoiceId,
  totalGross,
  paidAmount,
  settledOnBaseInvoice,
}) => {
  const isSettledOnBaseInvoice = settledOnBaseInvoice !== undefined;
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const { showSuccess } = useNotification();

  const { data: payments, isLoading, error } = useGetInvoicePaymentHistoryQuery(invoiceId);

  // Debug: Log payments to check if they're orphaned from deleted CashDocuments
  React.useEffect(() => {
    if (payments && payments.length > 0) {
      invoiceLogger.debug(
        '💰 Invoice Payments:',
        payments.map((p) => ({
          id: p.id,
          amount: p.amount,
          method: p.paymentMethod,
          note: p.note,
          date: p.paymentDate,
        }))
      );
    }
  }, [payments]);

  const handlePaymentSuccess = (isCashPayment: boolean) => {
    if (isCashPayment) {
      showSuccess('✅ Płatność zapisana. Dokument KP został automatycznie wystawiony.');
    } else {
      showSuccess('✅ Płatność zapisana pomyślnie.');
    }
  };

  // Calculate payment summary
  const summary: PaymentSummary = React.useMemo(() => {
    if (isSettledOnBaseInvoice) {
      return {
        totalGross,
        paidAmount,
        remainingAmount: 0,
        isOverpaid: false,
        overpaymentAmount: 0,
      };
    }

    const remaining = totalGross - paidAmount;
    return {
      totalGross,
      paidAmount,
      remainingAmount: Math.max(0, remaining),
      isOverpaid: paidAmount > totalGross,
      overpaymentAmount: Math.max(0, paidAmount - totalGross),
    };
  }, [totalGross, paidAmount, isSettledOnBaseInvoice]);

  const settlementLabel = React.useMemo(() => {
    if (!settledOnBaseInvoice) {
      return '';
    }

    const baseInvoice = settledOnBaseInvoice.baseInvoiceNumber
      ? ` ${settledOnBaseInvoice.baseInvoiceNumber}`
      : '';
    const paidOn = settledOnBaseInvoice.paidAt
      ? ` (opłacona ${dayjs(settledOnBaseInvoice.paidAt).format('DD.MM.YYYY')})`
      : '';

    return `Na fakturze bazowej${baseInvoice}${paidOn}`;
  }, [settledOnBaseInvoice]);

  const getPaymentTypeColor = (label: string) => {
    if (label === 'Płatność') return 'success';
    if (label === 'Zwrot') return 'error';
    return 'default';
  };

  const getPaymentMethodLabel = (paymentMethod: InvoicePaymentDto['paymentMethod']) => {
    if (paymentMethod === null || paymentMethod === undefined) {
      return '-';
    }

    if (paymentMethod === 0 || paymentMethod === '0') {
      return PaymentMethodTypeLabels[PaymentMethodType.Transfer];
    }

    if (paymentMethod === 1 || paymentMethod === '1') {
      return PaymentMethodTypeLabels[PaymentMethodType.Cash];
    }

    if (typeof paymentMethod === 'string') {
      const normalized = paymentMethod.trim().toLowerCase();

      if (normalized === PaymentMethodType.Transfer) {
        return PaymentMethodTypeLabels[PaymentMethodType.Transfer];
      }

      if (normalized === PaymentMethodType.Cash) {
        return PaymentMethodTypeLabels[PaymentMethodType.Cash];
      }

      return paymentMethod;
    }

    return String(paymentMethod);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Historia płatności</Typography>
        {!isSettledOnBaseInvoice && (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPaymentDialogOpen(true)}
              size="small"
            >
              Zarejestruj płatność
            </Button>
            {paidAmount > 0 && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<UndoIcon />}
                onClick={() => setRefundDialogOpen(true)}
                size="small"
              >
                Zarejestruj zwrot
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Payment Summary */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Wartość faktury:
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {summary.totalGross.toFixed(2)} PLN
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {isSettledOnBaseInvoice ? 'Rozliczenie:' : 'Wpłacono:'}
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="primary.main">
            {isSettledOnBaseInvoice ? settlementLabel : `${summary.paidAmount.toFixed(2)} PLN`}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
        >
          {summary.isOverpaid ? (
            <>
              <Typography variant="body2" color="error.main" fontWeight="bold">
                Nadpłata:
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="error.main">
                {summary.overpaymentAmount.toFixed(2)} PLN
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Pozostało do zapłaty:
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                color={summary.remainingAmount > 0 ? 'error.main' : 'success.main'}
              >
                {summary.remainingAmount.toFixed(2)} PLN
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      {/* Overpayment Warning */}
      {summary.isOverpaid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Faktura ma nadpłatę w wysokości {summary.overpaymentAmount.toFixed(2)} PLN. Rozważ
          wystawienie noty kredytowej lub zwrot środków klientowi.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Nie udało się pobrać historii płatności.{' '}
          {extractApiErrorMessage(
            error,
            'Odśwież stronę lub spróbuj ponownie później.'
          )}
        </Alert>
      )}

      {/* Payment History Table */}
      {!isLoading && !error && payments && payments.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data płatności</TableCell>
                <TableCell align="right">Kwota</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Metoda</TableCell>
                <TableCell>Notatka</TableCell>
                <TableCell>Zarejestrowano</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment: InvoicePaymentDto) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{dayjs(payment.paymentDate).format('DD.MM.YYYY')}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={payment.amount >= 0 ? 'success.main' : 'error.main'}
                    >
                      {payment.amount >= 0 ? '+' : ''}
                      {payment.amount.toFixed(2)} PLN
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.paymentTypeLabel}
                      size="small"
                      color={getPaymentTypeColor(payment.paymentTypeLabel)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getPaymentMethodLabel(payment.paymentMethod)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {payment.note || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(payment.createdAt).format('DD.MM.YYYY HH:mm')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        !isLoading &&
        !error &&
        !isSettledOnBaseInvoice && (
          <Alert severity="info">Brak zarejestrowanych płatności dla tej faktury.</Alert>
        )
      )}

      {/* Dialogs */}
      <RecordPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        invoiceId={invoiceId}
        remainingAmount={summary.remainingAmount}
        onSuccess={handlePaymentSuccess}
      />

      <RecordRefundDialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        invoiceId={invoiceId}
        paidAmount={summary.paidAmount}
        maxRefundAmount={summary.paidAmount}
      />
    </Box>
  );
};

export default PaymentSection;
